import { memo, useEffect, useMemo } from 'react';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { Operation } from 'shell/view-models/operation';
import { ValueDomainType } from 'shell/models/value-domain-type';
import { ItemVM } from 'shell/view-models/item';
import PmiTextField from 'shell/components/shared/text-field';
import { Autocomplete } from '@mui/material';

interface ListAnscValoriDominioProps {
  disabled?: boolean;
  value?: string;
  onChange: (value: string) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
  idDominio?: number;
  idValoreAnsc?: string;
}

const ListAnscValoriDominioComponent = ({
  disabled,
  onChange,
  required,
  value,
  error,
  label = 'Luogo di decesso',
  id = 'placeDeath',
  idDominio,
  idValoreAnsc,
}: ListAnscValoriDominioProps): React.ReactElement => {
  const [list, getList] = useHttpFetch<HttpResponse<Array<ValueDomainType>>, Array<ItemVM>>(``, {
    onBootstrap: false,
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
    transformer: (response) => {
      return response.data.payload.map((el) => ({
        id: el?.key?.idValore || '',
        value: el.descrizioneBreveValore || '',
      }));
    },
  });

  useEffect(() => {
    if (!idDominio) {
      return;
    }

    let url = `${__ACT_API_URL__}/valori-dominio-atti?filters=idDominio:${idDominio}:${Operation.EQUALS}`;

    if (idValoreAnsc) {
      url = `${url}&filters=idValoreAnsc:${idValoreAnsc}:${Operation.EQUALS}`;
    }

    getList({
      url,
    });
  }, [idDominio, idValoreAnsc]);

  const listData = (list.data as Array<ItemVM>) || [];

  const handleChange = (element: ItemVM | null): void => {
    onChange(element?.id?.toString() || '');
  };

  const defaultProps = useMemo(() => {
    const items = listData.slice() || [];

    return {
      options: items,
      getOptionLabel: (option: ItemVM) => option.value?.toString(),
    };
  }, [listData]);

  const selectedValue = (listData || []).find((elem) => elem.id === value);

  return (
    <Autocomplete
      {...defaultProps}
      value={{ id: selectedValue?.id || '', value: selectedValue?.value || '' }}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      disabled={disabled}
      clearOnBlur={false}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
          helperText=""
        />
      )}
    />
  );
};
ListAnscValoriDominioComponent.displayName = 'List Ansc Valori Dominio component';

const ListAnscValoriDominio = memo(ListAnscValoriDominioComponent);

export default ListAnscValoriDominio as typeof ListAnscValoriDominioComponent;
